export interface FilesystemMemberReference {
  readonly group: "root-codebase" | "world-files";
  readonly relativePath: string;
  readonly kind: "folder" | "member";
  readonly volumeUuid: string;
  readonly rootInode: string;
  readonly directoryInode: string;
  readonly name?: string;
}

/** Reserved leading slash distinguishes physical references from legacy paths.
 * A root identity supplies the namespace, including additional repositories. */
export function parseFilesystemMemberReference(memberId: string): FilesystemMemberReference | null {
  const match =
    /^(root-codebase|world-files):(\/(dir|file)\/([0-9a-f]{32})\/([1-9][0-9]{0,19})\/([1-9][0-9]{0,19})(?:\/([A-Za-z0-9_-]{1,340}))?)$/.exec(
      memberId,
    );
  if (!match || memberId.length > 512 || (match[3] === "file") !== (match[7] !== undefined))
    return null;
  const largest = 18446744073709551615n;
  if (BigInt(match[5]!) > largest || BigInt(match[6]!) > largest) return null;
  let name: string | undefined;
  if (match[7] !== undefined) {
    try {
      const encoded = match[7];
      const binary = atob(encoded.replaceAll("-", "+").replaceAll("_", "/"));
      if (btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "") !== encoded)
        return null;
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      name = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
      if (!name || name === "." || name === ".." || bytes.length > 255 || /[/\\\0]/.test(name))
        return null;
    } catch {
      return null;
    }
  }
  return {
    group: match[1] as FilesystemMemberReference["group"],
    relativePath: match[2]!,
    kind: match[3] === "dir" ? "folder" : "member",
    volumeUuid: match[4]!,
    rootInode: match[5]!,
    directoryInode: match[6]!,
    ...(name === undefined ? {} : { name }),
  };
}
